import { DefaultFocusStatusModel } from "../../../models/DefaultFocusStatusModel";
import { KeyboardNavigation } from "./KeyboardNavigation";

/**
 * CarrouselNavigation:
 * A specialized class handling keyboard focus for:
 *  - "Previous" arrow button
 *  - "Next" arrow button
 *  - Choice items in the carousel question
 */
export class CarrouselNavigation extends KeyboardNavigation {
    /**
     * Moves focus among elements within the same variable/question
     * when user presses arrow keys (Left/Right).
     */
    findNextFocusableElementWithinSameVariable(
        currentElement: DefaultFocusStatusModel,
        eventKey: string
    ): DefaultFocusStatusModel | null {
        return null;
    }

    /**
     * When user presses TAB, we jump to the next variable's first focusable element.
     */
    findNextFocusableElementWithinNextVariable(
        currentElement: DefaultFocusStatusModel
    ): DefaultFocusStatusModel | null {
        // Check if the current element is a carousel statement
        if (typeof currentElement.elementId === 'string' && currentElement.elementId.includes("statement_")) {
            // Special handling: Find any choice element in the entire array
            // regardless of position or variableId
            for (let i = 0; i < this.defaultFocusStatus.statuses.length; i++) {
                const element = this.defaultFocusStatus.statuses[i];
                // Look for any INPUT elements, or elements with common choice patterns in their IDs
                if (element.elementRef?.current && 
                    ((typeof element.elementId === 'string' && element.elementId.includes("INPUT")) ||
                     (typeof element.elementId === 'string' && element.elementId.includes("_C_")))
                     ){
                    return element;
                }
            }
        }
        
        // Default behavior for other elements
        return this.findNextFocusableElementWithinNextVariableCommon(currentElement);
    }

    /**
     * When user presses SHIFT+TAB, we jump to the previous variable's last focusable element.
     */
    findLastFocusableElementWithinPreviousVariable(
        currentElement: DefaultFocusStatusModel
    ): DefaultFocusStatusModel | null {
        return this.findLastFocusableElementWithinPreviousVariableCommon(currentElement);
    }

    // Override the common method to ensure visibility checks
    findNextFocusableElementWithinNextVariableCommon(
        currentElement: DefaultFocusStatusModel,
        variableIds: number[] = null,
    ): DefaultFocusStatusModel | null {
        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);
    
        // Check if current element is a carousel navigation arrow
        const isFromCarouselArrow = 
            (typeof currentElement.elementId === 'string' && 
             (currentElement.elementId.includes("CarrouselPrevArrow") || 
              currentElement.elementId.includes("CarrouselNextArrow"))) || false;

        for (let i = index + 1, len = this.defaultFocusStatus.statuses.length; i < len; i++) {
            const elementFocusStatus = this.defaultFocusStatus.statuses[i];
            const variableCondition =
                variableIds && variableIds.length > 0
                    ? !variableIds.includes(elementFocusStatus.variableId)
                    : elementFocusStatus.variableId !== currentElement.variableId;

            // For carousel arrows, we need special handling when the target would be another arrow
            if (isFromCarouselArrow) {
                const isTargetCarouselArrow = 
                    (typeof elementFocusStatus.elementId === 'string' && 
                     (elementFocusStatus.elementId.includes("CarrouselPrevArrow") || 
                      elementFocusStatus.elementId.includes("CarrouselNextArrow"))) || false;
                
                // If the target is another carousel arrow, check if it's focusable
                if (isTargetCarouselArrow && !this.isFocusable(elementFocusStatus)) {
                    // If not focusable, continue to the next element after the arrow
                    continue;
                }
            }

            if (
                elementFocusStatus.elementRef?.current &&
                ((elementFocusStatus.variableId !== 0 && variableCondition) ||
                (elementFocusStatus.variableId == currentElement.variableId && elementFocusStatus.elementRef?.current?.tagName == 'TEXTAREA')) &&
                this.isFocusable(elementFocusStatus)
            ) {
                return elementFocusStatus;
            }
        }
        return null;
    }    

    // Helper function to check if an element is focusable (not tabIndex="-1")
    isFocusable(element: DefaultFocusStatusModel): boolean {
        if (!element.elementRef?.current) return false;
        const tabIndex = element.elementRef.current.getAttribute('tabindex');
        const ariaDisabled = element.elementRef.current.getAttribute('aria-disabled');
        const ariaHidden = element.elementRef.current.getAttribute('aria-hidden');
        
        // Element is focusable if tabIndex is not -1 and not disabled/hidden
        return tabIndex !== '-1' && ariaDisabled !== 'true' && ariaHidden !== 'true';
    }

    findLastFocusableElementWithinPreviousVariableCommon(
        currentElement: DefaultFocusStatusModel,
        variableIds: number[] = null,
    ): DefaultFocusStatusModel | null {
        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);

        for (let i = index - 1; i >= 0; i--) {
            const elementFocusStatus = this.defaultFocusStatus.statuses[i];
            const variableCondition =
                variableIds && variableIds.length > 0
                    ? !variableIds.includes(elementFocusStatus.variableId)
                    : elementFocusStatus.variableId !== currentElement.variableId;

            const isTargetCarouselArrow = 
                (typeof elementFocusStatus.elementId === 'string' && 
                 (elementFocusStatus.elementId.includes("CarrouselPrevArrow") || 
                  elementFocusStatus.elementId.includes("CarrouselNextArrow"))) || false;
            
            // If the target is another carousel arrow, check if it's focusable
            if (isTargetCarouselArrow && !this.isFocusable(elementFocusStatus)) {
                // If not focusable, continue to the next element after the arrow
                continue;
            }
            

            if (
                elementFocusStatus.elementRef?.current &&
                elementFocusStatus.variableId !== 0 && 
                variableCondition &&
                this.isFocusable(elementFocusStatus)
            ) {
                return elementFocusStatus;
            }
        }
        return null;
    }
}

